import { useEffect, useState } from "react";
import { Trophy, XCircle, CheckCircle, Coins, WarningOctagon } from "@phosphor-icons/react";
import api from "../lib/api";

function MetricBar({ label, value }) {
  return (
    <div>
      <div className="flex justify-between mb-2"><span className="font-mono text-sm">{label}</span><span className="font-mono font-bold text-primary">{value ?? "—"}<span className="text-muted-foreground text-xs">/5</span></span></div>
      <div className="h-1.5 bg-secondary"><div className="h-full bg-primary transition-all" style={{ width: `${((value || 0) / 5) * 100}%` }} /></div>
    </div>
  );
}

function DirBlock({ title, data }) {
  if (!data || !data.count) return (
    <div className="bg-[#18181B] p-6"><div className="overline mb-4">{title}</div><div className="font-mono text-sm text-muted-foreground">no scores yet</div></div>
  );
  return (
    <div className="bg-[#18181B] p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="overline">{title}</div>
        <div className="font-mono text-xs text-muted-foreground">{data.count} reviews</div>
      </div>
      <div className="space-y-4 mb-6">
        {data.avg && Object.entries({ "Timing-fit": data.avg.timing_fit, "Naturalness": data.avg.naturalness, "Speaker fidelity": data.avg.speaker_fidelity, "Translation accuracy": data.avg.translation_accuracy }).map(([k, v]) => <MetricBar key={k} label={k} value={v} />)}
      </div>
      <div className="grid grid-cols-3 gap-px bg-border border border-border">
        <div className="bg-[#18181B] p-3"><div className="overline" style={{ fontSize: "0.5rem" }}>orch wins</div><div className="font-mono font-bold text-[#00E559]">{data.orchestrated_wins}</div></div>
        <div className="bg-[#18181B] p-3"><div className="overline" style={{ fontSize: "0.5rem" }}>baseline</div><div className="font-mono font-bold">{data.baseline_wins}</div></div>
        <div className="bg-[#18181B] p-3"><div className="overline" style={{ fontSize: "0.5rem" }}>win rate</div><div className="font-mono font-bold text-primary">{data.win_rate}%</div></div>
      </div>
    </div>
  );
}

export default function Scorecard() {
  const [sc, setSc] = useState(null);
  useEffect(() => { api.get("/scorecard").then(({ data }) => setSc(data)).catch(() => {}); }, []);
  if (!sc) return <div className="font-mono text-muted-foreground">loading scorecard…</div>;

  const pass = sc.gate_pass;

  return (
    <div>
      <div className="overline mb-2">aggregate · {sc.completed_runs} completed runs</div>
      <h1 className="font-mono font-extrabold tracking-tighter text-3xl mb-8">Decision scorecard</h1>

      <div data-testid="decision-gate" className="border-2 p-6 mb-10 flex flex-wrap items-center gap-6" style={{ borderColor: pass ? "#00E559" : "#FF3333", background: pass ? "rgba(0,229,89,0.05)" : "rgba(255,51,51,0.05)" }}>
        {pass ? <CheckCircle size={44} weight="fill" color="#00E559" /> : <XCircle size={44} weight="fill" color="#FF3333" />}
        <div className="flex-1 min-w-[240px]">
          <div className="font-mono font-extrabold tracking-tighter text-2xl" style={{ color: pass ? "#00E559" : "#FF3333" }}>{pass ? "ORCHESTRATION THESIS: PASS" : "THESIS NOT YET MET"}</div>
          <div className="text-sm text-muted-foreground mt-1 font-mono">Gate: orchestrated win-rate &gt; 50% AND avg cost &lt; $0.15/audio-min.</div>
        </div>
        <div className="flex items-center gap-2 font-mono text-sm"><Trophy size={18} className="text-primary" /> win-rate {sc.overall.win_rate ?? 0}%</div>
      </div>

      <div className="grid md:grid-cols-2 gap-px bg-border border border-border mb-10">
        <div className="bg-[#18181B] p-6">
          <div className="flex items-center gap-2 overline mb-3"><Coins size={13} /> avg cost / audio-min</div>
          <div className="font-mono font-extrabold text-3xl" style={{ color: sc.avg_cost_per_min == null ? "#A1A1AA" : sc.avg_cost_per_min < sc.cost_target ? "#00E559" : "#FF3333" }}>{sc.avg_cost_per_min != null ? `$${sc.avg_cost_per_min}` : "—"}</div>
          <div className="font-mono text-xs text-muted-foreground mt-1">target &lt; ${sc.cost_target}</div>
        </div>
        <div className="bg-[#18181B] p-6">
          <div className="flex items-center gap-2 overline mb-3"><WarningOctagon size={13} /> avg wasted gpu</div>
          <div className="font-mono font-extrabold text-3xl" style={{ color: sc.avg_wasted_compute_pct == null ? "#A1A1AA" : sc.avg_wasted_compute_pct < sc.wasted_target ? "#00E559" : "#FF3333" }}>{sc.avg_wasted_compute_pct != null ? `${sc.avg_wasted_compute_pct}%` : "—"}</div>
          <div className="font-mono text-xs text-muted-foreground mt-1">target &lt; {sc.wasted_target}%</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-px bg-border border border-border">
        <DirBlock title="Hindi → English" data={sc.hi_en} />
        <DirBlock title="English → Hindi" data={sc.en_hi} />
      </div>
    </div>
  );
}
